import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import SectionHeading from "@/components/SectionHeading";
import { MapPin, Ship, Droplets, FlaskConical, Mail, Package, FileCheck, Globe } from "lucide-react";
import heroImage from "@/assets/hero-olive-oil.webp";
import oliveGrove from "@/assets/olive-grove.webp";
import tunisianLandscape from "@/assets/tunisian-landscape.webp";
import chemlaliImg from "@/assets/chemlali.webp";
import chetouiImg from "@/assets/chetoui.webp";
import customBlendsImg from "@/assets/custom-blends.webp";
import lampanteImg from "@/assets/lampante.webp";

const stats = [
  { value: "8,000+ Tons Annual Supply Capacity", label: "Reliable large-volume supply" },
  { value: "Founded 2022", label: "Growing with trusted international buyers" },
  { value: "Sourcing Across Tunisia", label: "Stable origin and blending flexibility" },
  { value: "Export-Ready Worldwide", label: "Flexitank, tanker trucks & IBC" },
  { value: "Consistent quality across large volumes", label: "Samples available on request" },

];

const whyBlocks = [
  { icon: MapPin, title: "Nationwide Sourcing Network", text: "We work with producers and manufacturers across northern, central and southern Tunisia, allowing us to access different profiles, secure supply and stay commercially flexible." },
  { icon: Ship, title: "Export-Oriented Structure", text: "MM2A is built for professional trade. Our approach is designed around the needs of importers, bottlers, industrial buyers and long-term sourcing partners." },
  { icon: Package, title: "Bulk Supply Capability", text: "We focus on bulk olive oil supply adapted to industrial, commercial and international trade requirements." },
  { icon: FlaskConical, title: "Profile & Blending Expertise", text: "We work with Chemlali, Chetoui, Lampante and custom blends to match different buyer targets, market expectations and commercial strategies." },
];

const products = [
  { title: "Chemlali", text: "A mild, smooth and commercially versatile extra virgin olive oil. Chemlali's balanced profile and neutral character make it ideal for large-volume markets, private label bottling, and blending bases.", image: chemlaliImg,tags: ["Mild profile", "High volume", "Private label", "Blending base"] },
  { title: "Chetoui", text: "A bold, expressive extra virgin with strong intensity, distinctive bitterness, and robust peppery finish. Chetoui carries clear regional identity — ideal for premium positioning and specialty markets.", image: chetouiImg, tags: ["Robust profile", "Premium markets", "High polyphenols", "Specialty retail"]},
  { title: "Custom Blends", text: "Chemlali/Chetoui blends developed to your exact acidity, intensity, and sensory targets. We work with buyers to match profile requirements for specific destination markets or retail standards.", image: customBlendsImg,tags: ["Buyer-specified", "Acidity control", "Sensory matching", "Market-ready"] },
  { title: "Lampante", text: "Bulk crude olive oil supplied to refiners and industrial processors. Available in large volumes with consistent origin documentation and standard packaging for processing operations.", image: lampanteImg,tags: ["Bulk volumes", "Refiners", "Industrial use", "Origin docs"] },
];

const steps = [
  { num: "01 - Request", title: "Tell Us What You Need", icon: Mail },
  { num: "02 - Sample", title: "We Identify the Right Profile", icon: Droplets },
  { num: "03 - Validation", title: "We Coordinate Sample Evaluation", icon: FlaskConical },
  { num: "04 - Agreement", title: "We Align on Commercial Terms", icon: FileCheck },
  { num: "05 - Shipment", title: "We Prepare Export Execution", icon: Ship },
];

const Index = () => {
  return (
    <div className="pt-20">
      {/* Hero */}
<section className="relative min-h-[90vh] flex flex-col overflow-hidden">        <div className="absolute inset-0">
          <img fetchPriority="high" loading="eager" src={heroImage} alt="Olive oil production facility" className="w-full h-full object-cover" width={1920} height={1080} />
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal/80 via-charcoal/50 to-transparent" />
        </div>
<div className="relative z-10 container-max px-4 sm:px-6 lg:px-8 flex-grow flex items-center">          <div className="max-w-2xl">
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-cream mb-6 leading-tight">
            Tunisian Olive Oil Export, Sourcing & Blending
            </h1>
            <p className="text-cream/80 text-lg md:text-xl mb-8 leading-relaxed">
MM2A is a Tunisian olive oil export company specializing in bulk sourcing, custom blending, and international logistics. We serve importers, bottlers, distributors, refiners, and industrial buyers worldwide with consistent quality and full traceability from grove to shipment.            </p>
            <div className="flex flex-wrap gap-4 mb-10">
              <Link to="/contact">
                <Button size="lg" className="bg-gold text-charcoal hover:bg-gold-light font-semibold px-8">
                  Request Samples
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-cream/40 text-cream hover:bg-cream/10 px-8 bg-transparent">
                  Request a Quote
                </Button>
              </Link>
            </div>

          </div>
        </div>
        <div className="relative z-10 w-full py-6">
    <div className="w-full flex flex-wrap items-center justify-center gap-x-8 gap-y-4 px-4 text-cream/70 text-xs md:text-sm font-medium">
      <div className="flex items-center gap-2">
        <span className="text-gold">•</span> Bulk Olive Oil
      </div>
      <div className="flex items-center gap-2">
        <span className="text-gold">•</span> Export Expertise
      </div>
      <div className="flex items-center gap-2">
        <span className="text-gold">•</span> Traceability
      </div>
      <div className="flex items-center gap-2">
        <span className="text-gold">•</span> Long-Term Partnership
      </div>
      <div className="flex items-center gap-2">
        <span className="text-gold">•</span> Available in large volumes across Tunisia
      </div>
      <div className="flex items-center gap-2">
        <span className="text-gold">•</span> Bulk Supply Only – Flexitank, Tanker Trucks & IBC
      </div>
      <div className="flex items-center gap-2">
        <span className="text-gold">•</span> Year-round consistent profiles
      </div>
    </div>
  </div>
      </section>

      {/* Stats */}
     <section className="bg-primary overflow-hidden text-primary-foreground relative border-y border-white/5 w-full">
  <div className="container-max px-4 sm:px-6 lg:px-8 py-12 w-full">

    {/* Conteneur animé */}
    <div className="animate-marquee flex items-center">
      {/* On répète la liste pour créer la boucle infinie */}
      {[...stats, ...stats].map((s, index) => (
        <div 
          key={index} 
          className="flex flex-col items-center justify-center px-12 md:px-24 min-w-[200px] md:min-w-[350px]"
        >
          <p className="font-heading text-2xl md:text-3xl font-bold mb-2">
            {s.value}
          </p>
          <p className="text-primary-foreground text-sm">
            {s.label}
          </p>
        </div>
      ))}
    </div>

    {/* Masques de dégradé (Gradients) pour un effet de fondu sur les côtés */}
    <div className="pointer-events-none absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-primary to-transparent z-10"></div>
    <div className="pointer-events-none absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-primary to-transparent z-10"></div>
    
  </div>
</section>
      {/* About Preview */}
      <section className="section-padding">
        <div className="container-max grid md:grid-cols-2 gap-12 items-center">
          <div>
            <SectionHeading title="About MM2A Tunisian Export" centered={false} />
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>Founded in 2022 and based in Sfax, Tunisia, MM2A Tunisian Export works through a strong sourcing and production network covering the main olive-growing regions of the country.</p>
              <p>Our role is simple: to connect Tunisian olive oil supply with the needs of serious professional buyers through a reliable, structured and export-focused approach.</p>
              <p>From 2,000 tonnes in 2022 to over 8,000 tonnes today, our growth reflects buyer confidence, operational consistency and a clear long-term vision.</p>
            </div>
            <Link 
  to="/about" 
  aria-label="Learn more about MM2A Tunisian Export and our mission"
>
  <Button className="mt-6 bg-primary text-primary-foreground hover:bg-olive-dark">
    Learn More
  </Button>
</Link>
          </div>
          <div className="rounded-lg overflow-hidden shadow-lg">
            <img fetchPriority="high"  loading="eager" src={oliveGrove} alt="Tunisian olive grove" className="w-full h-full object-cover" width={1920} height={800} />
          </div>
        </div>
      </section>

      {/* Why Work With MM2A */}
      <section className="section-padding bg-muted">
        <div className="container-max">
          <SectionHeading title="Why Work With MM2A" />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyBlocks.map((b) => (
              <div key={b.title} className="bg-card p-8 rounded-lg shadow-sm">
                <b.icon className="w-10 h-10 text-olive-light mb-4" />
                <h3 className="font-heading text-xl font-semibold mb-3 text-foreground">{b.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{b.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Tunisian Olive Oil */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img fetchPriority="high" loading="eager" src={tunisianLandscape} alt="Tunisian olive landscape" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-charcoal/70" />
        </div>
        <div className="relative z-10 container-max px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-cream mb-6">Why Tunisian Olive Oil</h2>
          <p className="text-cream/80 text-lg max-w-3xl mx-auto leading-relaxed mb-4">
            Tunisia is one of the world's leading olive oil origins, known for its strong production capacity, Mediterranean climate and distinctive varietal diversity.
          </p>
          <p className="text-cream/70 text-lg max-w-3xl mx-auto leading-relaxed">
            With profiles such as Chemlali and Chetoui, Tunisian olive oil offers both commercial flexibility and strong regional identity — making it relevant for a wide range of international market needs.
          </p>
        </div>
      </section>

      {/* Products Preview */}
      <section className="section-padding">
        <div className="container-max">
          <SectionHeading title="Our Main Product Profiles" />
        <p className="text-black text-lg max-w-3xl mx-auto leading-relaxed mb-4 text-center">
              All oils are sourced directly from Tunisian producers and available in bulk. We supply consistent, documentated profiles ready for import, bottling, blending, or industrial refining.
          </p><br></br>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.map((p) => (
              <div key={p.title} className="group rounded-lg overflow-hidden shadow-sm border border-border bg-card">
                <div className="aspect-[4/3] overflow-hidden">
                  <img fetchPriority="high" loading="eager" src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-xl font-semibold mb-2 text-foreground">{p.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{p.text}</p><br></br>
                  <div className="flex flex-wrap gap-2 mb-4">
                 {p.tags?.map((tag) => (
                  <span 
                     key={tag} 
                    className="px-2 py-1 bg-olive/10 text-olive text-[10px] font-bold uppercase tracking-wider rounded"
                  >
                  {tag}
                   </span>
                  ))}
                </div>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/products">
              <Button className="bg-primary text-primary-foreground hover:bg-olive-dark">
                View All Products
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* How We Work */}
      <section className="section-padding bg-muted">
        <div className="container-max">
          <SectionHeading title="How We Work" />
          <div className="grid md:grid-cols-5 gap-6">
            {steps.map((s) => (
              <div key={s.num} className="text-center">
                <div className="w-16 h-16 rounded-full bg-primary text-primary-foreground flex items-center justify-center mx-auto mb-4">
                  <s.icon className="w-7 h-7" />
                </div>
                <p className="text-gold font-bold text-sm mb-1">{s.num}</p>
                <h4 className="font-heading text-base font-semibold text-foreground">{s.title}</h4>
              </div>
            ))}
          </div>
          <p className="text-center text-muted-foreground text-sm mt-10 max-w-2xl mx-auto">
            Our process is built to keep discussions clear, practical and efficient from the first contact to final shipment.
          </p>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="bg-primary text-primary-foreground section-padding">
        <div className="container-max text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">Let's Discuss Your Requirements</h2>
          <p className="text-primary-foreground/80 text-lg max-w-2xl mx-auto mb-8 leading-relaxed">
            If you are looking for a reliable Tunisian olive oil sourcing and export partner, we welcome serious business inquiries from importers, bottlers, distributors, refiners and industrial buyers.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-gold text-charcoal hover:bg-gold-light font-semibold px-10">
              Contact MM2A
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Index;
